import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { createSalonChannel, getListMaleOnline } from 'api';
import { TEXT_STRING, routes, storageKeys } from '../../constants';
import {
  Button,
  Header,
  Icon,
  Loading,
  MaleCard,
  Modal,
  Text,
} from 'components';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore, useUserStore } from 'store';
import { TCreateSalonChannel, TFilterMale, TMaleDetail } from 'types';
import { getApiErrorMessage, parseJSON } from 'utils';
import { toast } from 'react-toastify';
import MaleFilterModal from './MaleFilterModal';
import MaleItem from './MaleItem';
import { GuestRankStarDisplay } from './GuestRankStarDisplay';

const PAGE_SIZE = 28;
const LIST_MALE_STR = TEXT_STRING.LIST_MALE;
const COMMON_STR = TEXT_STRING.COMMON;

const initialFilter: TFilterMale = {
  areaIds: [],
  stars: [],
};

function mergeSavedMaleFilter(raw: string | null): TFilterMale {
  const parsed = parseJSON(raw) as Partial<TFilterMale> | null;
  if (!parsed) return { ...initialFilter };
  return {
    areaIds: parsed.areaIds ?? [],
    stars: parsed.stars ?? [],
  };
}

function ListMale() {
  const filterLocal = localStorage.getItem(storageKeys.FILTER_MALE);

  const [open, setOpen] = useState<boolean>(false);
  const [selectedMale, setSelectedMale] = useState<TMaleDetail | null>(null);
  const [showSalonRequiredPopup, setShowSalonRequiredPopup] = useState(false);
  const [isGroupSelectMode, setIsGroupSelectMode] = useState(false);
  const [selectedGroupMaleIds, setSelectedGroupMaleIds] = useState<number[]>(
    [],
  );
  const [filter, setFilter] = useState<TFilterMale>(() =>
    mergeSavedMaleFilter(filterLocal),
  );

  const navigate = useNavigate();
  const { user } = useUserStore();
  const viewerIsSalonMember = !!user?.isSalonMember;
  const { activityAreas } = useAppStore();
  const { mutateAsync: mutateCreateSalonChannel } = useMutation({
    mutationFn: (body: TCreateSalonChannel) => createSalonChannel(body),
  });

  const hasFilterSelection = useMemo(() => {
    const hasAreas = (filter.areaIds?.length ?? 0) > 0;
    const hasStars = (filter.stars?.length ?? 0) > 0;
    return hasAreas || hasStars;
  }, [filter]);

  const filterSummary = useMemo(() => {
    const areaNames = (activityAreas ?? [])
      .filter(({ id }) => filter.areaIds?.includes(id))
      .map(({ name }) => name)
      .join(', ');

    type GuestRankSegment = number;
    const rankSegments: GuestRankSegment[] = [...(filter.stars ?? [])];

    return (
      <div className="flex min-w-0 flex-1 flex-wrap items-center">
        {areaNames ? (
          <span key="areas" className="min-w-0 truncate">
            {areaNames}
          </span>
        ) : null}
        {areaNames && rankSegments.length > 0 ? (
          <span
            key="sep-areas-rank"
            className="shrink-0 leading-[16px] px-[4px]"
          >
            ·
          </span>
        ) : null}
        {rankSegments.map((item, index) => (
          <div
            key={`${String(item)}-${index}`}
            className="flex flex-wrap items-center shrink-0"
          >
            <GuestRankStarDisplay star={item} />
            {index < rankSegments.length - 1 ? (
              <span className="leading-[16px] px-[4px] shrink-0"> - </span>
            ) : null}
          </div>
        ))}
      </div>
    );
  }, [activityAreas, filter]);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: ['list_male', filter],
    queryFn: async ({ pageParam }) =>
      getListMaleOnline({
        currentPage: pageParam,
        pageSize: PAGE_SIZE,
        filter,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data?.length) return undefined;

      const currentPage = Number(lastPage.currentPage);
      const totalPage = Number(lastPage.totalPage);
      if (currentPage >= totalPage) return undefined;
      if (lastPage.data.length < PAGE_SIZE) return undefined;

      return currentPage + 1;
    },
  });

  const canLoadMore = !!hasNextPage && !isFetchingNextPage && !isFetchNextPageError;

  const handleLoadMore = useCallback(() => {
    if (!canLoadMore) return;
    void fetchNextPage();
  }, [canLoadMore, fetchNextPage]);

  const listView = useMemo<TMaleDetail[]>(
    () =>
      (data?.pages.map(({ data }) => data).flat() ?? []).filter(
        (male) => male.id !== user?.id,
      ),
    [data?.pages, user?.id],
  );

  const handleSearch = useCallback((filterValues: TFilterMale) => {
    localStorage.setItem(storageKeys.FILTER_MALE, JSON.stringify(filterValues));
    setFilter(filterValues);
    setOpen(false);
  }, []);

  const canStartDmWithSelectedMale = useMemo(
    () => viewerIsSalonMember && !!selectedMale?.isSalonMember,
    [selectedMale?.isSalonMember, viewerIsSalonMember],
  );

  const handleOpenMaleDm = useCallback(async () => {
    if (!selectedMale?.id) return;

    try {
      const channel = await mutateCreateSalonChannel({
        targetMaleIds: [selectedMale.id],
      });
      setSelectedMale(null);
      navigate(routes.CONVERSATION.replace(':id', channel.id));
    } catch (error) {
      toast.error(getApiErrorMessage(error, COMMON_STR.SOMETHING_WENT_WRONG));
    }
  }, [mutateCreateSalonChannel, navigate, selectedMale?.id]);

  const toggleGroupMale = useCallback((maleId: number) => {
    setSelectedGroupMaleIds((prev) =>
      prev.includes(maleId)
        ? prev.filter((selectedMaleId) => selectedMaleId !== maleId)
        : [...prev, maleId],
    );
  }, []);

  const handleToggleGroupSelectMode = useCallback(() => {
    setIsGroupSelectMode((prev) => !prev);
    setSelectedGroupMaleIds([]);
  }, []);

  const handleCreateSalonGroupChat = useCallback(async () => {
    if (selectedGroupMaleIds.length === 0) {
      toast.error(LIST_MALE_STR.SELECT_AT_LEAST_ONE_MEMBER);
      return;
    }

    try {
      const channel = await mutateCreateSalonChannel({
        targetMaleIds: selectedGroupMaleIds,
      });
      setIsGroupSelectMode(false);
      setSelectedGroupMaleIds([]);
      navigate(routes.CONVERSATION.replace(':id', channel.id));
    } catch (error) {
      toast.error(getApiErrorMessage(error, COMMON_STR.SOMETHING_WENT_WRONG));
    }
  }, [mutateCreateSalonChannel, navigate, selectedGroupMaleIds]);

  if (isLoading) {
    return <Loading fullScreen size="md" />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={<Text>{LIST_MALE_STR.TITLE}</Text>} />

      <div className="flex-1 overflow-y-auto" id="scrollListMale">
        <div className="flex flex-col gap-[18px] px-[16px]">
          <div className="sticky top-0 bg-white z-[1] flex flex-col gap-4 pt-4">
            <div className="cursor-pointer" onClick={() => setOpen(true)}>
              <div
                className={`w-full pointer-events-none pl-10px h-[40px] flex items-center rounded-lg ring-1 ring-neutral-300 focus-within:ring-2 focus-within:ring-primary pr-2`}
              >
                <div>
                  <Icon icon="searchInput" color="var(--color-neutral-300)" />
                </div>
                <div
                  className={`p-2.5 w-full min-w-0 flex-1 border-0 bg-transparent text-sm overflow-hidden ${
                    hasFilterSelection ? '' : 'text-neutral-300'
                  }`}
                >
                  {hasFilterSelection
                    ? filterSummary
                    : LIST_MALE_STR.FILTER_PLACEHOLDER}
                </div>
                {hasFilterSelection ? (
                  <div
                    className="pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      const reset: TFilterMale = {
                        areaIds: [],
                        stars: [],
                      };
                      setFilter(reset);
                      localStorage.setItem(
                        storageKeys.FILTER_MALE,
                        JSON.stringify(reset),
                      );
                    }}
                  >
                    <Icon icon="close" color="var(--color-neutral-300)" />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {!listView.length ? (
            <Text bold center className="mt-10">
              {COMMON_STR.NO_DATA}
            </Text>
          ) : (
            <InfiniteScroll
              next={handleLoadMore}
              hasMore={canLoadMore}
              loader={
                isFetchingNextPage ? (
                  <div className="py-[36px] flex justify-center items-center">
                    <Loading />
                  </div>
                ) : null
              }
              dataLength={listView.length}
              scrollThreshold={0.5}
              scrollableTarget="scrollListMale"
            >
              <div className="grid grid-cols-5 h-full gap-x-[12px] gap-y-[14px] pb-4">
                {listView.map((data) => {
                  const isSelectedGroupMale = selectedGroupMaleIds.includes(
                    data.id,
                  );
                  const isSelectableInGroupMode =
                    isGroupSelectMode && !!data.isSalonMember;
                  const containerClassName = !viewerIsSalonMember
                    ? 'relative cursor-pointer'
                    : isGroupSelectMode
                      ? isSelectableInGroupMode
                        ? 'relative cursor-pointer'
                        : 'relative cursor-not-allowed opacity-50'
                      : 'relative cursor-pointer';
                  const handleClick = !viewerIsSalonMember
                    ? () => setShowSalonRequiredPopup(true)
                    : isGroupSelectMode
                      ? isSelectableInGroupMode
                        ? () => toggleGroupMale(data.id)
                        : undefined
                      : () => setSelectedMale(data);

                  return (
                    <div
                      key={data.id}
                      className={containerClassName}
                      onClick={handleClick}
                    >
                      <MaleItem data={data} />
                      {isGroupSelectMode && isSelectableInGroupMode ? (
                        <div
                          className={`absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border ${
                            isSelectedGroupMale
                              ? 'border-primary bg-primary text-white'
                              : 'border-neutral-300 bg-white'
                          }`}
                        >
                          {isSelectedGroupMale ? (
                            <Icon icon="check" color="#ffffff" />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>

      {viewerIsSalonMember ? (
        <div className="w-full px-3 fixed bottom-[76px] left-1/2 -translate-x-1/2 z-[8] flex gap-2">
          {isGroupSelectMode ? (
            <>
              <Button
                className="w-full p-3 shadow-lg border border-primary"
                bold
                textColor='text-primary'
                bgColor='bg-primary-50'
                textSize="text-xs"
                onClick={handleToggleGroupSelectMode}
              >
                {LIST_MALE_STR.CANCEL_GROUP_CHAT}
              </Button>
              <Button
                className="w-full p-3 shadow-lg"
                bold
                textSize="text-xs"
                onClick={handleCreateSalonGroupChat}
              >
                {`${LIST_MALE_STR.CREATE_WITH_SELECTED_MEMBERS} (${selectedGroupMaleIds.length})`}
              </Button>
            </>
          ) : (
            <Button
              className="w-full mx-auto p-3 shadow-lg"
              bold
              textSize="text-xs"
              onClick={handleToggleGroupSelectMode}
            >
              {LIST_MALE_STR.CREATE_SALON_GROUP_CHAT}
            </Button>
          )}
        </div>
      ) : null}

      <MaleFilterModal
        openModal={open}
        filter={filter}
        activityAreas={activityAreas || []}
        closeModal={() => setOpen(false)}
        handleSearch={handleSearch}
      />

      <Modal
        open={showSalonRequiredPopup}
        onClose={() => setShowSalonRequiredPopup(false)}
        w="w-3/4"
        className="bg-white p-5 flex flex-col gap-5"
      >
        <Text bold center className="text-lg">
          {LIST_MALE_STR.SALON_REQUIRED_TITLE}
        </Text>
        <Text center fontSize={14}>
          {LIST_MALE_STR.SALON_REQUIRED_MESSAGE}
        </Text>
        <Button
          className="w-full py-4"
          onClick={() => setShowSalonRequiredPopup(false)}
          bold
        >
          {COMMON_STR.CLOSE}
        </Button>
      </Modal>

      <Modal
        open={!!selectedMale}
        onClose={() => setSelectedMale(null)}
        className="bg-white px-3 py-4"
        w="w-[calc(100%-12px)]"
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setSelectedMale(null)}
              className="cursor-pointer"
            >
              <Icon icon="close" />
            </button>
          </div>
          {selectedMale && (
            <>
              <MaleCard
                name={selectedMale.nickname}
                avatar={selectedMale.avatar}
                biography={selectedMale.biography}
                ranking={selectedMale.ranking}
                isSalonMember={!!selectedMale.isSalonMember}
                isSalonPremiumMember={!!selectedMale.isSalonPremiumMember}
              />
              {canStartDmWithSelectedMale && (
                <Button
                  className="w-full p-5"
                  bold
                  textSize="text-[14px]"
                  onClick={handleOpenMaleDm}
                >
                  {LIST_MALE_STR.START_DM}
                </Button>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default ListMale;
